import { Card } from '@/components/ui/card';
import { FilterChips, PageHeader, Toolbar, type FilterChip } from '@/components/app';
import { Pagination } from '@/components/shared/Pagination';
import { ExportCsvButton } from '@/components/shared/ExportCsvButton';
import type { User } from '@/api/users';
import type { CsvColumn } from '@/lib/csv';
import { USER_ROLE_LABELS, labelOf } from '@/lib/labels';
import { useUsers } from './hooks/useUsers';
import {
  UserFilters,
  UsersTable,
  UserDetailSheet,
  EditUserDialog,
  DeleteUserDialog,
  RestoreUserDialog,
  GrantPremiumDialog,
} from './components';
import { userStatus } from './format';

const fmt = (n: number) => new Intl.NumberFormat('ru-RU').format(n);
const iso = (d: Date | string | null) => (d ? new Date(d).toISOString() : '');

const CSV_COLUMNS: CsvColumn<User>[] = [
  { header: 'ID', accessor: (u) => u.id },
  { header: 'Логин', accessor: (u) => u.username },
  { header: 'Email', accessor: (u) => u.email },
  { header: 'Имя', accessor: (u) => u.firstName },
  { header: 'Фамилия', accessor: (u) => u.lastName },
  { header: 'Роль', accessor: (u) => labelOf(USER_ROLE_LABELS, u.role) },
  { header: 'Аноним', accessor: (u) => (u.is_anonymous ? 'да' : 'нет') },
  { header: 'Premium', accessor: (u) => (u.isSubscribed ? 'да' : 'нет') },
  { header: 'Premium до', accessor: (u) => iso(u.subscribedEnd) },
  { header: 'Статус', accessor: (u) => userStatus(u).label },
  { header: 'Последний вход', accessor: (u) => iso(u.lastLogin) },
  { header: 'Регистрация', accessor: (u) => iso(u.created_at) },
];

const SUBSCRIPTION_CHIP: Record<string, string> = { true: 'Premium', false: 'Free' };
const DELETION_CHIP: Record<string, string> = { true: 'Только удалённые', all: 'Включая удалённые' };

// Пропсы маршрута (тема) не нужны: цвета берутся из CSS-переменных.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function UsersPage(_props: Record<string, unknown>) {
  const u = useUsers();

  const chips: FilterChip[] = [];
  if (u.linkedUserId) {
    chips.push({ key: 'id', label: `Пользователь: ${u.linkedUserId.slice(0, 8)}…`, onRemove: u.clearLinkedUser, removeLabel: 'Убрать фильтр: пользователь' });
  }
  if (u.searchQuery) {
    chips.push({ key: 'search', label: `Поиск: ${u.searchQuery}`, onRemove: () => u.handleSearch(''), removeLabel: 'Убрать фильтр: поиск' });
  }
  if (u.subscriptionFilter !== 'all') {
    chips.push({ key: 'sub', label: SUBSCRIPTION_CHIP[u.subscriptionFilter] ?? u.subscriptionFilter, onRemove: () => u.handleSubscriptionFilter('all'), removeLabel: 'Убрать фильтр: статус' });
  }
  if (u.roleFilter !== 'all') {
    chips.push({ key: 'role', label: `Роль: ${labelOf(USER_ROLE_LABELS, u.roleFilter)}`, onRemove: () => u.handleRoleFilter('all'), removeLabel: 'Убрать фильтр: роль' });
  }
  if (u.deletionFilter !== 'false') {
    chips.push({ key: 'deleted', label: DELETION_CHIP[u.deletionFilter] ?? u.deletionFilter, onRemove: () => u.handleDeletionFilter('false'), removeLabel: 'Убрать фильтр: удалённые' });
  }

  const totalLabel = u.deletionFilter === 'true' ? 'удалённых аккаунтов' : 'аккаунтов';

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Пользователи"
        subtitle={
          u.isLoading && u.total === 0 ? (
            'Загрузка…'
          ) : (
            <>
              Найдено <span className="tabular-nums">{fmt(u.total)}</span> {totalLabel}
            </>
          )
        }
        actions={<ExportCsvButton fetchRows={u.fetchAllForExport} columns={CSV_COLUMNS} filename="users" disabled={u.total === 0} />}
      />

      <UserFilters
        searchQuery={u.searchQuery}
        roleFilter={u.roleFilter}
        subscriptionFilter={u.subscriptionFilter}
        deletionFilter={u.deletionFilter}
        onSearch={u.handleSearch}
        onRoleFilter={u.handleRoleFilter}
        onSubscriptionFilter={u.handleSubscriptionFilter}
        onDeletionFilter={u.handleDeletionFilter}
      />

      <Card className="overflow-hidden" role="region" aria-label="Список пользователей">
        <Toolbar
          title={
            <>
              Найдено <span className="tabular-nums">{fmt(u.total)}</span>
            </>
          }
          end={<span>Сортировка: по дате регистрации ↓</span>}
        >
          <FilterChips chips={chips} onReset={u.resetFilters} />
        </Toolbar>

        <UsersTable
          users={u.users}
          isLoading={u.isLoading}
          pageSize={u.pageSize}
          selectedId={u.viewingUserId}
          onView={u.handleView}
          onEdit={u.handleEdit}
          onDelete={u.setDeletingUser}
          onRestore={u.setRestoringUser}
          onGrantPremium={u.setPremiumUser}
          loadError={u.loadError}
          onRetry={u.fetchUsers}
          hasFilters={chips.length > 0}
          onResetFilters={u.resetFilters}
        />

        {!u.loadError && u.total > 0 && (
          <Pagination
            currentPage={u.currentPage}
            totalPages={u.totalPages}
            total={u.total}
            itemsPerPage={u.pageSize}
            onPageChange={u.setCurrentPage}
            onItemsPerPageChange={u.setPageSize}
          />
        )}
      </Card>

      <UserDetailSheet
        open={u.viewingUserId !== null}
        onClose={u.closeView}
        user={u.viewingUser}
        isSelf={u.viewingUserId === u.currentUserId}
        isLoading={u.isLoadingDetails}
        error={u.detailsError}
        onRetry={u.reloadDetails}
        busy={u.isQuickActionBusy}
        onEdit={u.handleEdit}
        onGrantPremium={u.setPremiumUser}
        onToggleBlock={u.handleToggleBlock}
        onToggleAdmin={u.handleToggleAdmin}
        onDelete={u.setDeletingUser}
        onRestore={u.setRestoringUser}
      />

      <EditUserDialog
        isOpen={u.isEditDialogOpen}
        onOpenChange={u.setIsEditDialogOpen}
        formData={u.editFormData}
        onFormDataChange={u.setEditFormData}
        onSave={u.handleSave}
        isSaving={u.isSaving}
        isSelf={u.editingUser?.id === u.currentUserId}
      />

      <DeleteUserDialog
        user={u.deletingUser}
        isDeleting={u.isDeleting}
        onOpenChange={() => u.setDeletingUser(null)}
        onConfirm={u.handleDelete}
      />

      <GrantPremiumDialog
        user={u.premiumUser}
        isSubmitting={u.isGrantingPremium}
        onOpenChange={(open) => {
          if (!open) u.setPremiumUser(null);
        }}
        onSubmit={u.handleGrantPremium}
      />

      <RestoreUserDialog
        user={u.restoringUser}
        isRestoring={u.isRestoring}
        onOpenChange={() => u.setRestoringUser(null)}
        onConfirm={u.handleRestore}
      />
    </div>
  );
}
